// PRC-040/041/042: estado real del motor de precios (bocetos G5 y G7).
// Preparándose (paso y progreso), listo o falló (con «Reintentar» y aviso a
// soporte). Un entrenamiento fallido nunca aparece como activo.
import React from 'react';
import { Box, LinearProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { getRetrainingStatus, type RetrainingStatusResponse } from '../../../services/diagnosisService';
import { startRetraining } from '../../../services/optimizationService';
import { JourneyButton, JourneyCard, JourneyText } from './JourneyBasics';
import { J } from '../journeyTokens';
import { SUPPORT_EMAIL } from '../journeyBilling';
import { withReturnTo } from '../navigation';
import { toSpanishNumber } from '../formatNumber';

export const ENGINE_STATUS_QUERY_KEY = ['retraining-status'];

export type EngineState = 'no_data' | 'preparing' | 'ready' | 'failed';

export const engineStateOf = (status: RetrainingStatusResponse | undefined): EngineState => {
  if (!status || status.status === 'not_found') return 'no_data';
  if (status.status === 'success' || status.status === 'success_with_warnings') return 'ready';
  if (status.status === 'running' || status.status === 'pending' || status.status === 'ready_to_train') return 'preparing';
  // PRC-041: cualquier otro estado (error o desconocido) es un fallo, nunca «activo».
  return 'failed';
};

// Tiempo que se muestra (PRC-041): de 30 min a 3 h según volumen.
export const ENGINE_ETA_TEXT = 'De 30 min a 3 h según el volumen y los datos de clientes y productos (unos 30 min con 50.000 ventas).';

export const useEngineStatus = (enabled = true) =>
  useQuery({
    queryKey: ENGINE_STATUS_QUERY_KEY,
    queryFn: getRetrainingStatus,
    enabled,
    refetchInterval: (query) => (['running', 'pending', 'ready_to_train'].includes(query.state.data?.status ?? '') ? 30_000 : false),
    staleTime: 15_000,
  });

type StepState = 'done' | 'current' | 'pending' | 'failed';

const StepDot: React.FC<{ state: StepState }> = ({ state }) => (
  <Box aria-hidden sx={{
    width: 22, height: 22, flex: '0 0 22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${state === 'done' ? J.ok : state === 'failed' ? J.danger : state === 'current' ? J.sky : J.borderStrong}`,
    bgcolor: state === 'done' ? 'rgba(74,222,128,.12)' : state === 'failed' ? 'rgba(248,113,113,.12)' : state === 'current' ? 'rgba(56,189,248,.12)' : 'transparent',
    color: state === 'done' ? J.okText : state === 'failed' ? J.dangerText : J.skyText, '& svg': { fontSize: 14 },
  }}>
    {state === 'done' && <CheckRoundedIcon />}
    {state === 'failed' && <ErrorOutlineRoundedIcon />}
    {state === 'current' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: J.sky }} />}
  </Box>
);

export interface EnginePanelProps {
  // Detalle de la última carga de ventas, si la hay.
  fileName?: string | null;
  receivedAt?: string | null;
  validLines?: number | null;
  discardedLines?: number | null;
  compact?: boolean;
}

const time = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '');

const EnginePanel: React.FC<EnginePanelProps> = ({ fileName, receivedAt, validLines, discardedLines, compact }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data } = useEngineStatus();
  const state = engineStateOf(data);
  const retry = useMutation({
    mutationFn: startRetraining,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ENGINE_STATUS_QUERY_KEY }),
  });

  if (state === 'no_data') return null;

  const trainingState: StepState = state === 'ready' ? 'done' : state === 'failed' ? 'failed' : 'current';
  const steps: Array<{ title: string; text: React.ReactNode; state: StepState }> = [
    { title: 'Archivo recibido', text: [fileName, time(receivedAt)].filter(Boolean).join(' · ') || 'Ventas cargadas', state: 'done' },
    { title: 'Datos revisados', text: validLines != null ? `${toSpanishNumber(validLines)} líneas válidas${discardedLines ? `, ${toSpanishNumber(discardedLines)} descartadas` : ''}` : 'Ventas listas para el análisis', state: 'done' },
    { title: 'Margin Center listo', text: 'Overview, Explorer y Control comercial ya muestran tus datos.', state: 'done' },
    {
      title: state === 'failed' ? 'El entrenamiento ha fallado' : state === 'ready' ? 'Modelo de precios entrenado' : 'Entrenando el modelo de precios',
      text: state === 'failed'
        ? <>No ha terminado bien. Vuelve a intentarlo; si se repite, escríbenos a <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: J.skyText }}>{SUPPORT_EMAIL}</a>.</>
        : state === 'ready' ? 'Listo con tus últimos datos.' : `${data?.started_at ? `Empezó a las ${time(data.started_at)}. ` : ''}${ENGINE_ETA_TEXT}`,
      state: trainingState,
    },
    // PRC-041: con un modelo listo de antes, la optimización sigue disponible mientras se reentrena.
    { title: 'Optimización disponible', text: data?.has_ready_model && state !== 'ready' ? 'Funciona con el modelo anterior mientras se prepara el nuevo.' : 'Evaluación rápida y lotes se activan solos al terminar.', state: state === 'ready' || data?.has_ready_model ? 'done' : 'pending' },
  ];
  const current = steps.findIndex((s) => s.state !== 'done');
  const stepNumber = current === -1 ? steps.length : current + 1;

  return (
    <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <Box>
          <JourneyText variant="h3" component="h2">{state === 'ready' ? 'Tu motor de precios está listo' : state === 'failed' ? 'No hemos podido preparar tu motor de precios' : 'Preparando tu motor de precios'}</JourneyText>
          {state === 'preparing' && <JourneyText variant="small">Puedes seguir trabajando; te avisamos por email al terminar.</JourneyText>}
          {state === 'failed' && <JourneyText variant="small">Margin Center sigue funcionando. Vuelve a intentarlo; si se repite, escríbenos a {SUPPORT_EMAIL}.</JourneyText>}
        </Box>
        <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: J.muted }}>Paso {stepNumber} de {steps.length}</Box>
      </Box>
      {state === 'preparing' && <LinearProgress aria-label="Entrenando el modelo de precios" sx={{ height: 6, borderRadius: 999, bgcolor: 'rgba(148,163,184,.14)', '& .MuiLinearProgress-bar': { bgcolor: J.sky } }} />}
      {!compact && (
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {steps.map((s) => (
            <Box component="li" key={s.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <StepDot state={s.state} />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: 14, fontWeight: 700, color: s.state === 'failed' ? J.dangerText : s.state === 'pending' ? J.muted : J.text }}>{s.title}</Box>
                <Box sx={{ fontSize: 13, color: J.muted, lineHeight: 1.5 }}>{s.text}</Box>
                {s.title === 'Margin Center listo' && (
                  <JourneyButton kind="quiet" small onClick={() => navigate(withReturnTo('/margin-center/overview', location.pathname))} sx={{ mt: 0.5, px: 0 }}>Abrir Overview</JourneyButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {state === 'failed' && (
        <Box>
          <JourneyButton small disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending ? 'Reintentando…' : 'Reintentar'}</JourneyButton>
          {retry.isError && (
            <JourneyText variant="small" sx={{ mt: 1, color: J.dangerText }}>
              No se ha podido volver a lanzar ahora. Escríbenos a {SUPPORT_EMAIL} y lo revisamos.
            </JourneyText>
          )}
        </Box>
      )}
    </JourneyCard>
  );
};

export default EnginePanel;
